/**
 * NFeDropdownMenu Component
 * 
 * Componente de menu dropdown para ações da NF-e.
 */

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Eye, Edit, XCircle, Send, RefreshCw, Download, PenLine } from 'lucide-react'
import './NFeDropdownMenu.css'

function NFeDropdownMenu({ nfe, onAction, onClose, buttonRef }) {
  const menuRef = useRef(null)

  useEffect(() => {
    // Posicionar o menu próximo ao botão
    const updatePosition = () => {
      if (menuRef.current && buttonRef) {
        const rect = buttonRef.getBoundingClientRect()
        menuRef.current.style.top = `${rect.bottom + 8}px`
        menuRef.current.style.left = `${rect.right - 180}px`
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    // Fechar ao clicar fora do dropdown
    const handleClickOutside = (event) => {
      // Não fechar se o clique foi em um botão do menu
      if (menuRef.current && menuRef.current.contains(event.target)) {
        return
      }
      
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef && !buttonRef.contains(event.target)) {
        onClose()
      }
    }

    // Adicionar listener após um pequeno delay para evitar fechar imediatamente
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100) // Aumentar delay para dar tempo do clique ser processado

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('mousedown', handleClickOutside)
      clearTimeout(timeoutId)
    }
  }, [buttonRef, onClose])

  const handleAction = (action) => {
    onAction(action, nfe)
    onClose()
  }

  // Verificar permissões baseadas no status
  const status = nfe.status ? String(nfe.status).trim().toLowerCase() : ''
  const canEdit = status === 'draft' // somente rascunho pode editar
  const canTransmit = status === 'saved' || status === 'error' // pode transmitir se salva ou com erro (para retransmitir)
  const canCancel = status === 'saved' || status === 'transmitted' // pode cancelar se salva ou transmitida
  const canView = true // Sempre pode visualizar
  const canConsultarStatus = status === 'saved' || status === 'transmitted' || status === 'error' // pode consultar se salva, transmitida ou com erro
  const canVisualizarDANFE = status === 'transmitted' // pode visualizar DANFE apenas se transmitida
  const canCorrigirRazaoSocial = status === 'transmitted' && nfe.customer_id

  const menuContent = (
    <div ref={menuRef} className="nfe-dropdown-menu-fixed">
      {canView && (
        <button 
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleAction('view')
          }}
        >
          <Eye size={16} />
          Visualizar
        </button>
      )}
      {canEdit && (
        <button onClick={() => handleAction('edit')}>
          <Edit size={16} />
          Editar
        </button>
      )}
      {canTransmit && (
        <button onClick={() => handleAction('transmit')}>
          <Send size={16} />
          Transmitir Nota
        </button>
      )}
      {canConsultarStatus && (
        <button onClick={() => handleAction('consultar-status')}>
          <RefreshCw size={16} />
          Consultar Status
        </button>
      )}
      {canVisualizarDANFE && (
        <button onClick={() => handleAction('visualizar-danfe')}>
          <Download size={16} />
          Visualizar DANFE
        </button>
      )}
      {canCorrigirRazaoSocial && (
        <button onClick={() => handleAction('corrigir-razao-social')}>
          <PenLine size={16} />
          Corrigir razão social
        </button>
      )}
      {canCancel && (
        <button onClick={() => handleAction('cancel')} className="danger">
          <XCircle size={16} />
          Cancelar
        </button>
      )}
    </div>
  )

  // Renderizar usando portal para evitar problemas com overflow hidden dos containers pais
  return typeof document !== 'undefined' 
    ? createPortal(menuContent, document.body)
    : menuContent
}

export default NFeDropdownMenu
